class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.formId = this.dataset.formId;
    this.priceId = this.dataset.priceId;
    this.sectionId = this.dataset.sectionId;
    this.watchId = this.dataset.watchId;
  }

  connectedCallback() {
    // Move to the end of <body> so position:fixed can't be broken by any
    // ancestor with a CSS transform (e.g. Dawn's scroll-reveal animations).
    // Doing this triggers a disconnect+reconnect, which would otherwise
    // run all the setup below a second time — the guard below prevents that.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    if (this.initialized) return;
    this.initialized = true;

    this.form = document.getElementById(this.formId);
    // Prefer watching the real form (tightly scoped to just the buttons) —
    // fall back to the manual wrapper div only if the form isn't found
    this.watchTarget = this.form || document.getElementById(this.watchId);
    this.realQuantityInput = this.form
      ? this.form.querySelector('input[name="quantity"]')
      : null;
    this.realSubmitButton = this.form
      ? this.form.querySelector('[name="add"]')
      : null;

    this.stickyQtyValue = this.querySelector('[data-sticky-qty-value]');
    this.stickyQtyMinus = this.querySelector('[data-sticky-qty-minus]');
    this.stickyQtyPlus = this.querySelector('[data-sticky-qty-plus]');
    this.stickyAddButton = this.querySelector('[data-sticky-add-to-cart]');
    this.stickyPrice = document.getElementById(`StickyPrice-${this.sectionId}`);

    this.bindEvents();
    this.observeVisibility();
    this.observePriceChanges();
  }

  bindEvents() {
    if (this.stickyQtyMinus) {
      this.stickyQtyMinus.addEventListener('click', () => this.adjustQuantity(-1));
    }
    if (this.stickyQtyPlus) {
      this.stickyQtyPlus.addEventListener('click', () => this.adjustQuantity(1));
    }
    if (this.stickyAddButton) {
      this.stickyAddButton.addEventListener('click', () => this.addToCart());
    }
  }

  adjustQuantity(delta) {
    if (!this.stickyQtyValue) return;

    let current = parseInt(this.stickyQtyValue.textContent, 10) || 1;
    current = Math.max(1, current + delta);
    this.stickyQtyValue.textContent = current;

    // Keep the real form's quantity input in sync so the real submit uses the right amount
    if (this.realQuantityInput) {
      this.realQuantityInput.value = current;
      this.realQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.realQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  addToCart() {
    if (!this.realSubmitButton) return;

    // Sync quantity one more time before triggering, in case the user
    // adjusted quantity on the real form instead of the sticky bar
    if (this.realQuantityInput && this.stickyQtyValue) {
      const qty = this.stickyQtyValue.textContent;
      this.realQuantityInput.value = qty;
      this.realQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.realQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Give any debounced quantity-handling scripts a tick to process
    // the update above before the form actually reads the value
    setTimeout(() => {
      this.realSubmitButton.click();
    }, 50);
  }

  observeVisibility() {
    if (!this.watchTarget || !('IntersectionObserver' in window)) {
      this.classList.add('sticky-atc--visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.remove('sticky-atc--visible');
          } else {
            this.classList.add('sticky-atc--visible');
          }
        });
      },
      {
        threshold: 0,
        // Treats the buttons as "out of view" a bit early — before their
        // top pixel literally crosses the viewport edge. Increase the
        // first value (in px) to trigger even earlier.
        rootMargin: '-300px 0px 0px 0px',
      }
    );

    observer.observe(this.watchTarget);
  }

  observePriceChanges() {
    const realPriceEl = document.getElementById(this.priceId);
    if (!realPriceEl || !this.stickyPrice) return;

    const syncPrice = () => {
      const text = realPriceEl.textContent.replace(/\s+/g, ' ').trim();
      // The real price block can contain "was" price + "now" price + a Sale
      // badge all concatenated. Grab just the last currency-looking value,
      // which is always the actual current price.
      const matches = text.match(/[\d,]+\.\d{2}\s?[A-Za-z]{0,4}/g);
      this.stickyPrice.textContent = matches && matches.length
        ? matches[matches.length - 1].trim()
        : text;
    };

    syncPrice();

    const observer = new MutationObserver(syncPrice);
    observer.observe(realPriceEl, { childList: true, subtree: true, characterData: true });
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);